import { getAccessToken } from './client.js'

const APPSYNC_URL = import.meta.env.VITE_APPSYNC_URL

function getRealtimeEndpoint() {
  const url = new URL(APPSYNC_URL)
  url.protocol = 'wss:'
  url.host = url.host.replace('appsync-api', 'appsync-realtime-api')
  url.pathname = url.pathname + '/realtime'
  return url.toString()
}

function b64(obj) {
  return btoa(JSON.stringify(obj))
}

// ---------------------------------------------------------------------------
// Shared WebSocket connection — all subscriptions multiplex over this single
// connection, matching how AWS Amplify works. Avoids concurrent-connection
// 401 errors from AppSync when multiple subscriptions start simultaneously.
// ---------------------------------------------------------------------------

let sharedWs = null
let wsState = 'disconnected' // 'connecting' | 'connected' | 'disconnected'
let retryTimer = null
const subs = new Map()       // subId → { query, variables, onData, onStatus }
const pendingStart = []       // subscriptions waiting for connection_ack

function ensureConnection() {
  if (wsState !== 'disconnected') return
  connect()
}

function maybeDisconnect() {
  if (subs.size > 0) return
  wsState = 'disconnected'
  clearTimeout(retryTimer)
  if (sharedWs) {
    sharedWs.close()
    sharedWs = null
  }
}

async function connect() {
  if (wsState !== 'disconnected') return
  wsState = 'connecting'

  let token
  try {
    token = await getAccessToken()
  } catch {
    wsState = 'disconnected'
    if (subs.size > 0) retryTimer = setTimeout(connect, 5000)
    return
  }

  if (!token) {
    wsState = 'disconnected'
    if (subs.size > 0) retryTimer = setTimeout(connect, 5000)
    return
  }

  if (subs.size === 0) { wsState = 'disconnected'; return }

  const host = new URL(APPSYNC_URL).host
  const header = b64({ Authorization: token, host })
  const realtimeUrl = `${getRealtimeEndpoint()}?header=${header}&payload=${b64({})}`

  const ws = new WebSocket(realtimeUrl, ['graphql-ws'])
  sharedWs = ws

  ws.onopen = () => {
    ws.send(JSON.stringify({ type: 'connection_init' }))
  }

  ws.onmessage = async (event) => {
    let msg
    try { msg = JSON.parse(event.data) } catch { return }

    if (msg.type === 'connection_ack') {
      wsState = 'connected'
      // Register all current subscriptions
      for (const [id, sub] of subs) {
        startSubscription(ws, id, sub)
      }
      // Drain any that were added while connecting
      while (pendingStart.length > 0) {
        const id = pendingStart.shift()
        const sub = subs.get(id)
        if (sub) startSubscription(ws, id, sub)
      }
    } else if (msg.type === 'start_ack' && msg.id) {
      const sub = subs.get(msg.id)
      if (sub) sub.onStatus?.({ connected: true })
    } else if (msg.type === 'connection_error') {
      // Auth rejected at connection level — close and let onclose retry
      ws.close()
    } else if (msg.type === 'error' && msg.id) {
      // Subscription-level error — notify and retry that subscription
      const sub = subs.get(msg.id)
      if (sub) {
        sub.onStatus?.({ connected: false })
        setTimeout(() => { if (subs.has(msg.id) && sharedWs === ws) startSubscription(ws, msg.id, sub) }, 3000)
      }
    } else if (msg.type === 'data' && msg.id) {
      const sub = subs.get(msg.id)
      const data = msg.payload?.data
      if (sub && data) sub.onData(data)
    }
    // 'ka' (keep-alive) is intentionally ignored
  }

  ws.onerror = () => { /* onclose always fires after onerror */ }

  ws.onclose = () => {
    sharedWs = null
    wsState = 'disconnected'
    // Notify all subscriptions they are disconnected
    for (const sub of subs.values()) {
      sub.onStatus?.({ connected: false })
    }
    // Reconnect if there are still active subscriptions
    if (subs.size > 0) retryTimer = setTimeout(connect, 3000)
  }
}

async function startSubscription(ws, id, sub) {
  if (ws.readyState !== WebSocket.OPEN) return
  let token
  try { token = await getAccessToken() } catch { return }
  if (!token) return
  const host = new URL(APPSYNC_URL).host
  ws.send(JSON.stringify({
    id,
    type: 'start',
    payload: {
      data: JSON.stringify({ query: sub.query, variables: sub.variables }),
      extensions: {
        authorization: { Authorization: token, host },
      },
    },
  }))
}

// ---------------------------------------------------------------------------
// Public API — same signature as before
// ---------------------------------------------------------------------------

/**
 * Subscribe to an AppSync GraphQL subscription.
 *
 * All subscriptions share a single WebSocket connection. The connection is
 * opened lazily on first subscribe and closed when the last one unsubscribes.
 *
 * @param {string} query  GraphQL subscription document
 * @param {object} variables
 * @param {function} onData  Called with the full data object from the subscription payload
 * @param {object} options
 * @param {function} options.onStatus  Called with { connected: boolean } on status changes
 * @returns {function} unsubscribe
 */
export function subscribe(query, variables = {}, onData, { onStatus } = {}) {
  const id = crypto.randomUUID()
  subs.set(id, { query, variables, onData, onStatus })

  if (wsState === 'connected' && sharedWs) {
    startSubscription(sharedWs, id, subs.get(id))
  } else if (wsState === 'connecting') {
    pendingStart.push(id)
  } else {
    ensureConnection()
  }

  return () => {
    subs.delete(id)
    if (sharedWs && sharedWs.readyState === WebSocket.OPEN) {
      sharedWs.send(JSON.stringify({ id, type: 'stop' }))
    }
    onStatus?.({ connected: false })
    maybeDisconnect()
  }
}
